import React from "react";
import { Box, Grid, Typography, Button, TextField } from "@material-ui/core";
import { InputAdornment } from "@material-ui/core";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AutoComplete from "@material-ui/lab/Autocomplete";
import { useStyles } from "../../index";
import IndFlag from "../../../../assets/svgs/ind.svg";

/**
 * SimpleDetailsStage Component
 * 
 * Handles the simple details step of the registration form.
 * Includes grade selection, course selection, board selection, and phone number input.
 * 
 * Separation of Concerns:
 * - Manages its own Formik form and validation
 * - Only passes essential data (grade, batch, boards, phoneNumber) to parent on submit
 * - No business logic or API calls
 */
const SimpleDetailsStage = ({
  initialValues,
  schoolOrCollege,
  school_classes,
  college_classes,
  branchesData,
  boards_options,
  branch,
  onNext,
  onBack,
  onFieldChange,
  openSnackbar,
  renderOptionText,
}) => {
  const classes = useStyles();

  // Validation schema for this stage only
  const validationSchema = Yup.object().shape({
    grade: Yup.string().required("Class is required"),
    batch: Yup.string().nullable(),
    boards: Yup.string().required("Board is required"),
    phoneNumber: Yup.string()
      .matches(/^[0-9]+$/, "Phone number must be a number")
      .min(10, "Phone number must be 10 characters")
      .max(10, "Phone number must be 10 characters")
      .required("Phone number is required"),
  });

  const currentBatches =
    branch && branchesData[branch] && branchesData[branch].batches
      ? branchesData[branch].batches[initialValues.grade] || []
      : [];

  const getFilteredGrades = () => {
    if (!branch || !branchesData[branch]) return [];
    const allClasses = branchesData[branch].classes || [];
    if (schoolOrCollege === "school") {
      return allClasses.filter((cls) => school_classes.includes(cls));
    } else if (schoolOrCollege === "college") {
      return allClasses.filter((cls) => college_classes.includes(cls));
    }
    return allClasses;
  };

  const filteredGrades = getFilteredGrades();

  return (
    <Formik
      initialValues={{
        grade: initialValues.grade || null,
        batch: initialValues.batch || null,
        boards: initialValues.boards || null,
        phoneNumber: initialValues.phoneNumber || "",
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values) => {
        // Only pass essential data to parent
        onNext({
          grade: values.grade,
          batch: values.batch,
          boards: values.boards,
          phoneNumber: values.phoneNumber,
        });
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
        return (
          <Form>
            <Box maxWidth={320} margin="auto">
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box marginTop={1}>
                    <Typography variant="body2" className={classes.label}>
                      Seeking Admission<b style={{ color: "red" }}>*</b>
                    </Typography>
                  </Box>
                  <AutoComplete
                    id="grade"
                    name="grade"
                    options={filteredGrades}
                    value={values.grade}
                    onChange={(e, value) => {
                      setFieldValue("batch", null);
                      setFieldValue("grade", value);
                      if(onFieldChange) {
                        onFieldChange("grade", value);
                      }
                    }}
                    onOpen={() => {
                      if (values.grade && !filteredGrades.includes(values.grade)) {
                        setFieldValue("grade", null);
                        setFieldValue("batch", null);
                        if(onFieldChange) {
                          onFieldChange("grade", null);
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        color="secondary"
                        fullWidth
                        error={touched.grade && Boolean(errors.grade)}
                        helperText={touched.grade && errors.grade}
                      />
                    )}
                    renderOption={(option) => (
                      <Typography variant="button" className={classes.option}>
                        {renderOptionText(option)}
                      </Typography>
                    )}
                  />
                </Grid>
                {currentBatches.length > 1 && (
                  <Grid item xs={12}>
                    <Box marginTop={1}>
                      <Typography variant="body2" className={classes.label}>
                        Select Course<b style={{ color: "red" }}>*</b>
                      </Typography>
                    </Box>
                    <AutoComplete
                      id="batch"
                      name="batch"
                      options={currentBatches}
                      value={values.batch || ""}
                      onChange={(e, value) => {
                        setFieldValue("batch", value);
                        if(onFieldChange) {
                          onFieldChange("batch", value);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          color="secondary"
                          fullWidth
                          error={touched.batch && Boolean(errors.batch)}
                          helperText={touched.batch && errors.batch}
                        />
                      )}
                      renderOption={(option) => (
                        <Typography variant="button" className={classes.option}>
                          {option}
                        </Typography>
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" component="p" className={classes.label}>
                    Board of the school
                    <br />
                    the student last studied
                    <b style={{ color: "red" }}>*</b>
                  </Typography>
                  <AutoComplete
                    id="boards"
                    name="boards"
                    options={boards_options}
                    value={values.boards}
                    onChange={(e, value) => {
                      setFieldValue("boards", value);
                      if(onFieldChange) {
                        onFieldChange("boards", value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        color="secondary"
                        error={touched.boards && Boolean(errors.boards)}
                        helperText={touched.boards && errors.boards}
                      />
                    )}
                    renderOption={(option) => (
                      <Typography variant="button" className={classes.option}>
                        {option}
                      </Typography>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" component="p" className={classes.label}>
                    Parent Phone No (Whatsapp)
                    <b style={{ color: "red" }}>*</b>
                  </Typography>
                  <TextField
                    fullWidth
                    id="phoneNumber"
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    variant="outlined"
                    color="secondary"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            gridGap={10}
                          >
                            <Box>
                              <img src={IndFlag} width="40" alt="india flag" />
                            </Box>
                            <Typography variant="button">+91</Typography>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="space-between" mt={2}>
                <Button
                  type="button"
                  onClick={onBack}
                  variant="outlined"
                  className={classes.blueGradientBtn2}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  className={classes.blueGradientBtn}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SimpleDetailsStage;

